<script setup>
import { computed, ref, onMounted, nextTick, getCurrentInstance } from 'vue';
const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  type: { type: String, default: 'text' },
  customInputClass: { type: [String, Object, Array], default: '' },
  placeholder: { type: String, default: '' },
  label: { type: String, default: '' },
  isStar: { type: Boolean, default: false },
  id: { type: String, default: '' },
  message: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
  min: { type: String, default: '' },
  autofocus: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:modelValue',
  'blur',
  'input',
  'focus',
  'enter',
]);

// Generate a unique ID per component instance when `id` prop is not provided.
const { uid } = getCurrentInstance();
const uniqueId = computed(() => props.id || `input-${uid}`);

const isFocused = ref(false);
const inputRef = ref(null);

const messageClass = computed(() => {
  switch (props.messageType) {
    case 'error':
      return 'text-n-ruby-9 dark:text-n-ruby-9';
    case 'success':
      return 'text-n-teal-10 dark:text-n-teal-10';
    default:
      return 'text-n-slate-11 dark:text-n-slate-11';
  }
});

const inputBorderClass = computed(() => {
  switch (props.messageType) {
    case 'error':
      return 'border-n-ruby-7 dark:border-n-ruby-7 focus:border-n-ruby-8 dark:focus:border-n-ruby-8 focus:shadow-[0_0_0_3px_rgba(220,38,38,0.1)]';
    default:
      return 'border-n-slate-6 dark:border-n-slate-6 focus:border-n-brand dark:focus:border-n-brand focus:shadow-[0_0_0_3px_rgba(32,107,196,0.1)]';
  }
});

const handleInput = event => {
  emit('update:modelValue', event.target.value);
  emit('input', event);
};

const handleFocus = event => {
  emit('focus', event);
  isFocused.value = true;
};

const handleBlur = event => {
  emit('blur', event);
  isFocused.value = false;
};

const handleEnter = event => {
  emit('enter', event);
};

onMounted(() => {
  if (props.autofocus) {
    nextTick(() => {
      inputRef.value?.focus();
    });
  }
});
</script>

<template>
  <div class="relative flex flex-col min-w-0 gap-1">
    <label
      v-if="label"
      :for="uniqueId"
      class="mb-2 text-sm font-medium text-n-slate-12 dark:text-n-slate-12"
    >
      {{ label }}<span v-if="isStar" class="text-n-ruby-9 ms-1">*</span>
    </label>
    <slot name="prefix" />
    <input
      :id="uniqueId"
      ref="inputRef"
      :value="modelValue"
      :class="[
        customInputClass,
        inputBorderClass,
        {
          error: messageType === 'error',
          focus: isFocused,
        },
      ]"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      :min="['date', 'datetime-local', 'time'].includes(type) ? min : undefined"
      class="block w-full text-sm h-10 px-3 py-2 border border-solid rounded-md bg-white dark:bg-n-slate-1 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-9 dark:placeholder:text-n-slate-9 disabled:cursor-not-allowed disabled:bg-n-slate-3 disabled:text-n-slate-10 dark:disabled:bg-n-slate-2 dark:disabled:text-n-slate-9 text-n-slate-12 dark:text-n-slate-12 transition-all duration-150 focus:outline-none"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
      @keyup.enter="handleEnter"
    />
    <p
      v-if="message"
      class="min-w-0 mt-2 mb-0 text-xs break-words"
      :class="messageClass"
    >
      {{ message }}
    </p>
  </div>
</template>
